import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserContext = createContext(null);

// Global axios interceptor: on 401 Unauthorized, clear session and force re-login.
// Placed at module scope so it registers once, not on every render.
let sessionExpiredHandled = false;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && !sessionExpiredHandled) {
      // Ignore 401 coming from the login endpoint itself (that's a bad password, not an expired session)
      const url = error?.config?.url || '';
      const isLoginRequest = url.includes('/auth/login') || url.includes('/auth/change-password');
      if (!isLoginRequest && localStorage.getItem('crm_token')) {
        sessionExpiredHandled = true;
        try { toast.error('Tu sesión ha caducado, vuelve a iniciar sesión.'); } catch (_) { /* ignore */ }
        localStorage.removeItem('crm_token');
        localStorage.removeItem('crm_user');
        delete axios.defaults.headers.common['Authorization'];
        delete axios.defaults.headers.common['X-User-Email'];
        // Small delay so the toast can render before redirect
        setTimeout(() => {
          window.location.href = '/login';
        }, 700);
      }
    }
    return Promise.reject(error);
  }
);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load users list (for selectors in the app, not for login)
  const loadUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }, []);

  // Load stored auth data on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('crm_token');
    const storedUser = localStorage.getItem('crm_user');
    
    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(userData);
        
        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        axios.defaults.headers.common['X-User-Email'] = userData.email;
        
        // Load users list
        loadUsers();
      } catch (e) {
        // Invalid stored data, clear it
        localStorage.removeItem('crm_token');
        localStorage.removeItem('crm_user');
      }
    }
    setLoading(false);
  }, [loadUsers]);

  // Login function - called after successful API login
  const login = (accessToken, userData) => {
    sessionExpiredHandled = false;  // reset so future 401s trigger the redirect again
    setToken(accessToken);
    setUser(userData);
    localStorage.setItem('crm_token', accessToken);
    localStorage.setItem('crm_user', JSON.stringify(userData));
    
    // Set axios headers
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    axios.defaults.headers.common['X-User-Email'] = userData.email;
    
    // Load users list for app usage
    loadUsers();
  };

  // Update token (after password change)
  const updateToken = (newToken) => {
    setToken(newToken);
    localStorage.setItem('crm_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    
    // Also update user to clear force_password_change flag
    if (user) {
      const updatedUser = { ...user, force_password_change: false };
      setUser(updatedUser);
      localStorage.setItem('crm_user', JSON.stringify(updatedUser));
    }
  };

  // Logout function
  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-User-Email'];
  };

  // Role-based checks
  const isAdmin = user?.role === 'admin';
  const isEditor = user?.role === 'editor' || user?.role === 'admin';
  const isViewer = user?.role === 'viewer';
  const isAuthenticated = !!user && !!token;
  const needsPasswordChange = user?.force_password_change === true;

  // Filter users to get only editors and admins for selector components
  const editorUsers = users.filter(u => u.role === 'editor' || u.role === 'admin');

  return (
    <UserContext.Provider value={{
      user,
      currentUser: user,
      token,
      users,
      editorUsers,
      loading,
      login,
      logout,
      updateToken,
      isAdmin,
      isEditor,
      isViewer,
      isAuthenticated,
      needsPasswordChange
    }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
